import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getNotes, addNote, updateNote, deleteNote } from '../api';
import {
  convertDateForApi,
  formatDateForDisplay,
  getTodayByLocale,
  getMonthStartByLocale,
  getDateConfig,
  switchDateLocale,
  usePagination,
} from '../utils';
import DatePicker from 'react-multi-date-picker';

function Notes({ dateLocale }) {
  const dateConfig = getDateConfig(dateLocale);

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    note_text: '',
    note_date: getTodayByLocale(dateLocale),
  });
  const [filters, setFilters] = useState({
    startDate: getMonthStartByLocale(dateLocale),
    endDate: getTodayByLocale(dateLocale),
  });
  const prevLocaleRef = useRef(dateLocale);
  const notesPagination = usePagination(notes);
  const goToNotesPage = notesPagination.goToPage;

  const renderPaginationControls = (pagination) => {
    if (pagination.totalItems === 0) {
      return null;
    }

    return (
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between border-top px-3 py-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">تعداد در صفحه</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pagination.pageSize}
            onChange={(e) => pagination.setPageSize(Number(e.target.value))}
          >
            {pagination.pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            نمایش {pagination.rangeStart} تا {pagination.rangeEnd} از {pagination.totalItems}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.prevPage}
            disabled={!pagination.canGoPrev}
          >
            قبلی
          </button>
          <span className="text-muted small">
            صفحه {pagination.currentPage} از {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.nextPage}
            disabled={!pagination.canGoNext}
          >
            بعدی
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const prevLocale = prevLocaleRef.current;
    if (prevLocale !== dateLocale) {
      setFormData((prev) => ({
        ...prev,
        note_date: switchDateLocale(prev.note_date, prevLocale, dateLocale),
      }));
      setFilters((prev) => ({
        ...prev,
        startDate:
          prev.startDate
            ? switchDateLocale(prev.startDate, prevLocale, dateLocale)
            : getMonthStartByLocale(dateLocale),
        endDate:
          prev.endDate
            ? switchDateLocale(prev.endDate, prevLocale, dateLocale)
            : getTodayByLocale(dateLocale),
      }));
      prevLocaleRef.current = dateLocale;
    }
  }, [dateLocale]);

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.startDate) {
        params.start_date = convertDateForApi(filters.startDate, dateLocale);
      }
      if (filters.endDate) {
        params.end_date = convertDateForApi(filters.endDate, dateLocale);
      }
      const response = await getNotes(params);
      setNotes(response.data);
      setError('');
      goToNotesPage(1);
    } catch (err) {
      setError('خطا در بارگذاری نت‌ها');
    } finally {
      setLoading(false);
    }
  }, [filters, dateLocale, goToNotesPage]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.note_text.trim()) {
      setError('متن نت الزامی است');
      return;
    }
    if (!formData.note_date) {
      setError('تاریخ الزامی است');
      return;
    }

    try {
      const noteData = {
        note_text: formData.note_text,
        note_date: convertDateForApi(formData.note_date, dateLocale),
      };

      if (editingId) {
        await updateNote(editingId, noteData);
        setSuccess('نت با موفقیت ویرایش شد');
      } else {
        await addNote(noteData);
        setSuccess('نت با موفقیت اضافه شد');
      }

      setFormData({
        note_text: '',
        note_date: getTodayByLocale(dateLocale),
      });
      setEditingId(null);
      loadNotes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(editingId ? 'خطا در ویرایش نت' : 'خطا در افزودن نت');
    }
  };

  const handleEdit = (note) => {
    setEditingId(note.id);
    setFormData({
      note_text: note.note_text,
      note_date: note.note_date,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({
      note_text: '',
      note_date: getTodayByLocale(dateLocale),
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این نت را حذف کنید؟')) {
      return;
    }

    try {
      await deleteNote(id);
      setSuccess('نت با موفقیت حذف شد');
      loadNotes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در حذف نت');
    }
  };

  const resetFilters = () => {
    setFilters({
      startDate: getMonthStartByLocale(dateLocale),
      endDate: getTodayByLocale(dateLocale),
    });
  };

  if (loading && notes.length === 0) {
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="fw-bold text-muted">در حال بارگذاری...</span>
      </div>
    );
  }

  return (
    <div className="d-grid gap-4">
      <div className="card border-0 shadow-sm">
        <div className="card-body d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div>
            <h2 className="h5 mb-1">مدیریت نت‌ها</h2>
            <p className="text-muted small mb-0">ثبت و مدیریت یادداشت‌های روزانه</p>
          </div>
          <span className="badge bg-primary text-white px-3 py-2">{notes.length} نت فعال</span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError('')}
          />
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccess('')}
          />
        </div>
      )}

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-bottom-0 pb-0">
              <h2 className="h5 mb-0">{editingId ? 'ویرایش نت' : 'افزودن نت جدید'}</h2>
              <p className="text-muted small mb-0">
                {editingId ? 'ویرایش یادداشت انتخاب شده' : 'اطلاعات یادداشت جدید را وارد کنید'}
              </p>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-12">
                  <label className="form-label">تاریخ نت</label>
                  <DatePicker
                    value={formData.note_date}
                    onChange={(date) => {
                      if (date) {
                        setFormData({ ...formData, note_date: date });
                      }
                    }}
                    calendar={dateConfig.calendar}
                    locale={dateConfig.locale}
                    calendarPosition="bottom-right"
                    inputClass="form-control"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">متن نت</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    value={formData.note_text}
                    onChange={(e) => setFormData({ ...formData, note_text: e.target.value })}
                    placeholder="متن یادداشت را وارد کنید"
                    required
                  />
                </div>
                <div className="col-12">
                  {editingId ? (
                    <div className="d-grid gap-2">
                      <button type="submit" className="btn btn-primary w-100">
                        ویرایش نت
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-100"
                        onClick={handleCancelEdit}
                      >
                        انصراف
                      </button>
                    </div>
                  ) : (
                    <button type="submit" className="btn btn-primary w-100">
                      ثبت نت
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-8">
          <div className="d-grid gap-3">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-bottom-0 pb-0">
                <h2 className="h6 mb-1">فیلتر لیست نت‌ها</h2>
                <p className="text-muted small mb-0">بازه مورد نظر را انتخاب کنید</p>
              </div>
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-5">
                    <label className="form-label">از تاریخ</label>
                    <DatePicker
                      value={filters.startDate}
                      onChange={(date) =>
                        setFilters((prev) => ({
                          ...prev,
                          startDate: date || null,
                        }))
                      }
                      calendar={dateConfig.calendar}
                      locale={dateConfig.locale}
                      calendarPosition="bottom-right"
                      inputClass="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-5">
                    <label className="form-label">تا تاریخ</label>
                    <DatePicker
                      value={filters.endDate}
                      onChange={(date) =>
                        setFilters((prev) => ({
                          ...prev,
                          endDate: date || null,
                        }))
                      }
                      calendar={dateConfig.calendar}
                      locale={dateConfig.locale}
                      calendarPosition="bottom-right"
                      inputClass="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <button type="button" className="btn btn-outline-secondary w-100" onClick={resetFilters}>
                      بازنشانی
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="h5 mb-1">لیست نت‌ها</h2>
                  <p className="text-muted small mb-0">پیگیری یادداشت‌های ثبت شده</p>
                </div>
                <span className="badge bg-secondary text-white px-3 py-2">
                  {notes.length} مورد ثبت شده
                </span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">ردیف</th>
                        <th scope="col">تاریخ</th>
                        <th scope="col">متن نت</th>
                        <th scope="col" className="text-end">عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notesPagination.totalItems === 0 && (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-4">
                            هنوز نتی ثبت نشده است
                          </td>
                        </tr>
                      )}
                      {notesPagination.paginatedItems.map((note, index) => (
                        <tr key={note.id}>
                          <td>
                            <span className="badge bg-primary text-white">
                              {notesPagination.rangeStart + index}
                            </span>
                          </td>
                          <td>{formatDateForDisplay(note.note_date, dateLocale)}</td>
                          <td>
                            <div className="text-break">{note.note_text}</div>
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-2 justify-content-end">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(note)}
                              >
                                ویرایش
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(note.id)}
                              >
                                حذف
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderPaginationControls(notesPagination)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Notes;

